import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { useContract } from '@/hooks/useContract';
import { CLASS_INFO, GameState } from '@/lib/types/game';
import WalletButton from '@/components/web3/WalletButton';
import NetworkBadge from '@/components/web3/NetworkBadge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { PartySprites, FourCrystals } from '@/components/game/PixelSprites';

interface SlotData {
  active: boolean;
  state: GameState | null;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isConnected } = useWallet();
  const { getSlotInfo, getGameStateForSlot, selectSlot, deleteSlot } = useContract();

  const [slots, setSlots] = useState<SlotData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [deleteConfirmSlot, setDeleteConfirmSlot] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  // Load slots
  const loadSlots = async () => {
    if (!isConnected) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const slotInfo = await getSlotInfo();
      if (!slotInfo) {
        setSlots(Array(8).fill({ active: false, state: null }));
        return;
      }

      // Fetch game states for all active slots
      const slotData: SlotData[] = await Promise.all(
        slotInfo.slots.map(async (active, index) => {
          if (!active) {
            return { active: false, state: null };
          }
          try {
            const state = await getGameStateForSlot(index);
            return { active: true, state };
          } catch {
            return { active: true, state: null };
          }
        })
      );

      setSlots(slotData);
    } catch (error) {
      console.error('Failed to load slots:', error);
      setSlots(Array(8).fill({ active: false, state: null }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      loadSlots();
    } else {
      setIsLoading(false);
    }
  }, [isConnected]);

  const handlePlaySlot = async (slot: number) => {
    setIsSelecting(true);
    setShowLoadModal(false);
    try {
      const result = await selectSlot(slot);
      if (result.success) {
        navigate('/game');
      }
    } finally {
      setIsSelecting(false);
    }
  };

  const handleDeleteSlot = async (slot: number) => {
    setIsDeleting(true);
    try {
      const result = await deleteSlot(slot);
      if (result.success) {
        setDeleteConfirmSlot(null);
        await loadSlots();
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const formatPlayTime = (blocks: number) => {
    const seconds = blocks * 6;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const hasAnyGame = slots.some(s => s.active);
  const activeSlots = slots.filter(s => s.active);

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center p-4 bg-gradient-to-b from-[#1a1a2e] to-[#0f0e17]">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5 bg-[url('/crystal-pattern.svg')] bg-repeat" />

      <div className="relative z-10 max-w-4xl mx-auto w-full">
        {/* Header */}
        <div className="absolute top-4 right-4 flex items-center gap-4">
          <WalletButton />
          <NetworkBadge />
        </div>

        {/* Hero Section */}
        <div className="flex flex-col items-center justify-center min-h-screen gap-6 px-4">

          {/* Four Crystals */}
          <FourCrystals size={48} className="mb-4" />

          {/* Title */}
          <div className="text-center">
            <h1 className="text-pixel-xl text-[#e94560] text-shadow mb-2 tracking-wider">
              CRYSTALS OF POLKADOT
            </h1>
            <p className="text-pixel-sm text-[#eaeaea] text-shadow">
              An On-Chain JRPG Tribute to Final Fantasy I
            </p>
          </div>

          {/* Character Sprites */}
          <PartySprites size={56} className="my-4" />

          {/* Subtitle */}
          <p className="text-game text-lg text-center text-shadow max-w-xl mx-auto text-gray-300">
            Four crystals have lost their light. Four fiends of chaos spread darkness across the land.
            Will you become the Warriors of Light?
          </p>

          {/* Main Card */}
          <Card className="max-w-md w-full mt-4">
            {!isConnected ? (
              <div className="flex flex-col items-center gap-4 py-6">
                <p className="text-game text-base text-center mb-2 text-gray-300">
                  Connect your wallet to begin your adventure on the Polkadot AssetHub.
                </p>
                <WalletButton />
              </div>
            ) : isLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner text="Loading..." />
              </div>
            ) : (
              <div className="space-y-4 py-2">
                {/* New Game Button */}
                <Button
                  variant="primary"
                  size="lg"
                  className="w-full"
                  onClick={() => navigate('/new-game')}
                >
                  New Game
                </Button>

                {/* Load Game Button - only show if has saves */}
                {hasAnyGame && (
                  <Button
                    variant="secondary"
                    size="lg"
                    className="w-full"
                    onClick={() => setShowLoadModal(true)}
                  >
                    Load Game ({activeSlots.length} save{activeSlots.length !== 1 ? 's' : ''})
                  </Button>
                )}
              </div>
            )}
          </Card>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 w-full">
            <Card title="100% On-Chain">
              <p className="text-game text-sm text-gray-300">
                All game logic runs on Polkadot AssetHub smart contracts. Your progress is truly yours.
              </p>
            </Card>
            <Card title="Classic JRPG">
              <p className="text-game text-sm text-gray-300">
                Turn-based combat, character classes, magic spells, and epic boss battles inspired by FF1.
              </p>
            </Card>
            <Card title="Provably Fair">
              <p className="text-game text-sm text-gray-300">
                Commit-reveal randomness ensures fair combat and loot. No hidden server manipulation.
              </p>
            </Card>
          </div>
        </div>
      </div>

      {/* Load Game Modal */}
      <Modal
        isOpen={showLoadModal}
        onClose={() => setShowLoadModal(false)}
        title="Load Game"
      >
        <div className="modal-section">
          <p className="text-game text-base text-gray-300 mb-6">
            Select a save file to continue your adventure.
          </p>

          <div className="space-y-4 max-h-[28rem] overflow-y-auto pr-2">
            {slots.map((slot, index) => {
              if (!slot.active || !slot.state) return null;
              return (
                <div
                  key={index}
                  className="ff-border slot-card bg-[#0f3460]"
                >
                  {/* Header row */}
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-pixel-sm text-[#e94560]">
                      Slot {index + 1}
                    </span>
                    <span className="text-game text-sm text-gray-400">
                      {formatPlayTime(slot.state.playTime)}
                    </span>
                  </div>

                  {/* Party */}
                  <div className="flex flex-wrap mb-3">
                    {slot.state.party.map((char, i) => (
                      <span key={i} className="party-tag">
                        {CLASS_INFO[char.class]?.name || '???'}
                      </span>
                    ))}
                  </div>

                  {/* Stats row */}
                  <div className="flex gap-6 text-game text-sm text-gray-300 mb-4 pb-3 border-b border-[#533483]/40">
                    <span>
                      <span className="text-gray-500">Lv.</span> {slot.state.party[0]?.level || 1}
                    </span>
                    <span>
                      <span className="text-[#ffd700]">{slot.state.gil.toLocaleString()}</span> Gil
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="primary"
                      size="md"
                      className="flex-1"
                      onClick={() => handlePlaySlot(index)}
                    >
                      Load
                    </Button>
                    <Button
                      variant="danger"
                      size="md"
                      onClick={() => {
                        setShowLoadModal(false);
                        setDeleteConfirmSlot(index);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={deleteConfirmSlot !== null}
        onClose={() => setDeleteConfirmSlot(null)}
        title="Delete Save?"
      >
        <div className="modal-section">
          <p className="text-game text-xl text-red-400 mb-4">
            Are you sure you want to delete Slot {(deleteConfirmSlot ?? 0) + 1}?
          </p>
          <p className="text-game text-base text-gray-400 mb-8">
            This action cannot be undone. All progress will be permanently lost.
          </p>
          <div className="flex justify-end gap-4">
            <Button
              variant="secondary"
              size="md"
              onClick={() => setDeleteConfirmSlot(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="md"
              onClick={() => deleteConfirmSlot !== null && handleDeleteSlot(deleteConfirmSlot)}
              isLoading={isDeleting}
            >
              Delete Forever
            </Button>
          </div>
        </div>
      </Modal>

      {/* Loading overlay when selecting slot */}
      {isSelecting && (
        <LoadingSpinner
          size="lg"
          text="Loading your adventure..."
          fullScreen
        />
      )}
    </div>
  );
};

export default Home;
